import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { getCookieObject } from "../utils/cookie";
import { jwtDecode } from "jwt-decode";
import { services } from "../services";
import Select from "react-select";
import { toast } from "react-toastify";
import { allCourseIds, draCourseIds, bcbfCourseIds } from "../config";

const PartnerBatchAttendance = () => {
  const [batches, setBatches] = useState<any[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  const [studentsConfig, setStudentsConfig] = useState<any[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [batchSlots, setBatchSlots] = useState<any[]>([]);
  const [batchUsers, setBatchUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedCourseGroup, setSelectedCourseGroup] = useState<any>(null);
  const [batchInputValue, setBatchInputValue] = useState("");

  // Fetch batches when component mounts
  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await services.requestSvc.post("/get-iibf-course-batches", { course_ids: allCourseIds });
      let allBatches = [];
      if (response.success) {
        allBatches = response.data?.flatMap((c: any) => {
            return (c.batches || []).map((b: any) => ({ ...b, course_id: c.id }));
        }) || [];
      } else if (Array.isArray(response)) {
        allBatches = response.flatMap((c: any) => {
            return (c.batches || []).map((b: any) => ({ ...b, course_id: c.id }));
        });
      }

      const access_token = getCookieObject().access_token;
      if (access_token) {
        const decodedUser: any = jwtDecode(access_token);
        if (decodedUser?.role === "PARTNER_ADMIN" && decodedUser?.partnerId) {
          try {
            const summaryRes = await services.requestSvc.post("/partners-candidate-summary-simple", {
              courseIds: allCourseIds,
              partnerId: decodedUser.partnerId
            });
            const partnerBatchIds = new Set<string>();
            let summaryData = Array.isArray(summaryRes) ? summaryRes : summaryRes?.data;
            if (Array.isArray(summaryData)) {
               summaryData.forEach((item: any) => {
                  if (item.batchIds && Array.isArray(item.batchIds)) {
                     item.batchIds.forEach((id: string) => partnerBatchIds.add(id.toString()));
                  }
               });
               allBatches = allBatches.filter((b: any) => partnerBatchIds.has(b.id.toString()));
            }
          } catch(summaryErr) {
            console.error("Failed to fetch partner summary for batch filtering", summaryErr);
          }
        }
      }

      setBatches(allBatches);
    } catch (err) {
      console.error("Error fetching batches", err);
      toast.error("Error fetching batches");
    }
  };

  const fetchAttendance = async (batchId: any) => {
    setLoading(true);
    setBatchUsers([]);
    setBatchSlots([]);
    try {
      const access_token = getCookieObject().access_token;
      let userIds = undefined;

      if (access_token) {
        const decodedUser: any = jwtDecode(access_token);
        if (decodedUser?.role === "PARTNER_ADMIN" && decodedUser?.partnerId) {
          const candidateListRes = await services.requestSvc.post("/partners-candidate-list", {
            filter: { 
              partnerId: decodedUser.partnerId,
              batchIds: [batchId].map(Number)
            },
            paginationParams: { pageIndex: 0, pageSize: 10000 },
            sort: [{ column: "createdAt", order: "desc" }]
          });
          const validUsers = candidateListRes.data || [];
          setBatchUsers(validUsers);
          userIds = validUsers.map((user: any) => parseInt(user.stUserId, 10)).filter((id: number) => !isNaN(id));
          
          if (userIds.length === 0) {
            setAttendanceRecords([]);
            setBatchSlots([]);
            setLoading(false);
            return;
          }
        }
      }

      const attResponse = await services.requestSvc.post("/get-batch-attendance", {
        batch_id: batchId,
        user_ids: userIds
      });
      if (attResponse) {
        const responseData = attResponse.data || attResponse;
        if (responseData && !Array.isArray(responseData) && responseData.slots) {
           setAttendanceRecords(responseData.attendances || []);
           setBatchSlots(responseData.slots || []);
        } else {
           // fallback for older local un-updated backends
           setAttendanceRecords(Array.isArray(responseData) ? responseData : []);
           setBatchSlots([]);
        }
      }
    } catch (err) {
      console.error("Error fetching attendance", err);
      toast.error("Error fetching attendance");
    }
    setLoading(false);
  };

  const handleBatchChange = (selectedOption: any) => {
    setSelectedBatch(selectedOption);
    if (selectedOption?.value) {
      fetchAttendance(selectedOption.value);
    } else {
      setAttendanceRecords([]);
      setBatchUsers([]);
      setBatchSlots([]);
    }
  };

  const batchOptions = useMemo(() => {
    let filteredBatches = batches;

    let options = filteredBatches.slice().sort((a: any, b: any) => (b.id || 0) - (a.id || 0)).map(b => ({
      label: `${b.id} - ${b.batch_name}`,
      value: b.id,
      batch: b
    }));

    if (batchInputValue) {
      options = options.filter(o => o.label.toLowerCase().includes(batchInputValue.toLowerCase()));
    }

    return options.slice(0, 100);
  }, [batches, batchInputValue]);

  const uniqueUsers = useMemo(() => {
    if (batchUsers.length > 0) return batchUsers.map(u => ({ ...u, id: String(u.stUserId) }));
    
    // fallback if we ONLY have attendance records but no candidate list API support
    const usersMap = new Map();
    attendanceRecords.forEach(r => {
      if (!usersMap.has(String(r.user_id))) {
        usersMap.set(String(r.user_id), { id: String(r.user_id), name: `Unknown User`, stUserId: String(r.user_id) });
      }
    });
    return Array.from(usersMap.values());
  }, [batchUsers, attendanceRecords]);

  const dateColumns = useMemo(() => {
    if (batchSlots && batchSlots.length > 0) {
       // Using raw backend slots layout to 1:1 perfectly map with Trainer portal
       return batchSlots.map(s => {
          if (!s.slot_date) return new Date();
          return new Date(s.slot_date);
       }).sort((a, b) => a.getTime() - b.getTime());
    }

    if (selectedBatch?.batch?.from_date && selectedBatch?.batch?.to_date) {
      const start = new Date(selectedBatch.batch.from_date);
      const end = new Date(selectedBatch.batch.to_date);
      
      const dates = [];
      let current = new Date(start);
      // Safety limit of 60 days
      while (current <= end && dates.length < 60) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      return dates;
    }
    
    // Ultimate Fallback: Derive distinct dates from attendance records if everything else is missing!
    const uniqueDates = new Set<string>();
    if (attendanceRecords && attendanceRecords.length > 0) {
      attendanceRecords.forEach(r => {
        if (r.created_at) {
          // toDateString formats to something like "Mon Jan 01 2024" matching exact day boundaries cleanly
          uniqueDates.add(new Date(r.created_at).toDateString());
        }
      });
      return Array.from(uniqueDates).map(d => new Date(d)).sort((a, b) => a.getTime() - b.getTime());
    }

    return [];
  }, [selectedBatch, batchSlots, attendanceRecords]);

  const formatDateLabel = (date: Date) => {
    const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: '2-digit' };
    return new Intl.DateTimeFormat('en-GB', opts).format(date);
  };

  const isSameDay = (date1: Date, dateString: string) => {
    if (!dateString) return false;
    const date2 = new Date(dateString);
    return (
       date1.getFullYear() === date2.getFullYear() &&
       date1.getMonth() === date2.getMonth() &&
       date1.getDate() === date2.getDate()
    );
  };

  return (
    <div className="p-4 bg-gray-50 h-full overflow-y-auto w-full">
      <h2 className="text-2xl font-bold mb-4 text-[#0F41C1]">Batch Attendance</h2>
      
      <div className="bg-white p-4 rounded-lg shadow-md mb-6 w-full max-w-lg">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Select Batch
        </label>
        <Select
          options={batchOptions}
          value={selectedBatch}
          onChange={handleBatchChange}
          onInputChange={(val) => setBatchInputValue(val)}
          placeholder="Search batches..."
          isClearable
          className="w-full"
        />
      </div>

      <div className="bg-white rounded-lg shadow-md w-full overflow-hidden">
        <div className="p-4 bg-[#EDF1FA] border-b">
          <h3 className="font-semibold text-[#0F41C1]">
            Attendance {selectedBatch?.batch?.batch_name && `for ${selectedBatch.batch.batch_name}`}
          </h3>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading attendance...</div>
          ) : uniqueUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {selectedBatch ? "No users or attendance found for this batch" : "Please select a batch to view attendance"}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#f9fafb]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">User</th>
                  {dateColumns.length > 0 ? dateColumns.map((date, idx) => (
                    <th key={idx} className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                      <div>{formatDateLabel(date)}</div>
                      <div className="text-gray-400 mt-1">10:00</div>
                    </th>
                  )) : (
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                      Overall Attendance
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {uniqueUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap border-b">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                        <div className="text-gray-500 text-xs">{(user.email && user.email !== "null") ? user.email.toLowerCase() : user.mobileNumber}</div>
                      </div>
                    </td>
                    {dateColumns.length > 0 ? dateColumns.map((date, idx) => {
                      const record = attendanceRecords.find(r => String(r.user_id) === String(user.id) && isSameDay(date, r.created_at));
                      return (
                        <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium border-b border-l">
                           {!record ? (
                              <span className="text-gray-400">NA</span>
                           ) : (
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${record.attendance_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {record.attendance_status ? 'Present' : 'Absent'}
                              </span>
                           )}
                        </td>
                      );
                    }) : (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-l">
                         {(() => {
                            const count = attendanceRecords.filter(r => String(r.user_id) === String(user.id)).length;
                            if (count === 0) {
                               return <span className="px-2 inline-flex text-xs leading-5 font-medium rounded-full bg-gray-100 text-gray-600">Not Marked</span>;
                            }
                            return (
                               <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  {count} records found
                               </span>
                            );
                         })()}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerBatchAttendance;
